import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Flame, Target, TrendingUp, BookOpen, ArrowRight, MessageCircle } from "lucide-react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import BadgesPanel from "@/components/BadgesPanel";
import WeeklyGoal from "@/components/WeeklyGoal";
import StreakReminder from "@/components/StreakReminder";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/dashboard");
      setData(r.data);
    } catch (err) {
      console.error("dashboard load failed:", err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="max-w-7xl mx-auto p-8">Loading…</div>;

  const radarData = data.topics.slice(0, 8).map((t) => ({ topic: t.topic, mastery: t.mastery }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-neutral-500 uppercase">Hi {data.user.name?.split(" ")[0]}</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold">Your learning today</h1>
        </div>
        <div className="flex gap-3">
          <Link data-testid="dash-cta-practice" to="/practice" className="nb-btn"><Target className="w-4 h-4" /> Practice now</Link>
          <Link data-testid="dash-cta-tutor" to="/tutor" className="nb-btn nb-btn-secondary"><MessageCircle className="w-4 h-4" /> Ask AI Tutor</Link>
        </div>
      </div>

      <StreakReminder />
      <WeeklyGoal />

      <div className="grid md:grid-cols-4 gap-4">
        <StatCard testid="stat-mastery" icon={<TrendingUp />} label="Avg. mastery" value={`${data.avg_mastery}%`} bg="bg-white" />
        <StatCard testid="stat-streak" icon={<Flame />} label="Streak" value={`${data.streak_days} days`} bg="bg-[var(--sun)]" />
        <StatCard testid="stat-attempts" icon={<BookOpen />} label="Questions solved" value={data.total_attempts} bg="bg-white" />
        <StatCard testid="stat-correct" icon={<Target />} label="Correct" value={data.correct_attempts} bg="bg-[var(--mint)]" />
      </div>

      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-7 nb-card p-6">
          <h2 className="text-2xl font-extrabold">Mastery map</h2>
          <p className="text-sm text-neutral-600">Where you're strong — and where we're focusing next.</p>
          <div className="h-72 mt-4">
            {radarData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#1C1C1C" />
                  <PolarAngleAxis dataKey="topic" tick={{ fill: "#1C1C1C", fontSize: 12, fontWeight: 600 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#6B7280" }} />
                  <Radar name="Mastery" dataKey="mastery" stroke="#FF5E5B" fill="#FF5E5B" fillOpacity={0.5} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState />
            )}
          </div>
        </div>

        <div className="md:col-span-5 nb-card p-6 bg-[var(--bg-highlight)]">
          <h2 className="text-2xl font-extrabold">Weak points</h2>
          <p className="text-sm text-neutral-600">Drill these to level up fast.</p>
          <div className="mt-4 space-y-3">
            {data.weak_points.length === 0 && (
              <div className="text-sm text-neutral-600">No weak points yet — start a practice session to map your skills.</div>
            )}
            {data.weak_points.map((w) => (
              <div key={w.topic} data-testid={`weak-${w.topic}`} className="bg-white nb-card p-4 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase font-bold text-neutral-500">{w.subject}</div>
                  <div className="font-extrabold text-lg">{w.topic}</div>
                  <div className="text-xs text-neutral-600">{w.attempts} attempts · {w.accuracy}% correct</div>
                </div>
                <div className="text-right">
                  <div className="nb-badge nb-tag-coral">{w.mastery}%</div>
                </div>
              </div>
            ))}
          </div>
          <Link data-testid="drill-weak-btn" to="/practice" className="nb-btn nb-btn-accent mt-4 w-full">
            Drill weak points <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <div className="nb-card p-6">
        <h2 className="text-2xl font-extrabold">All topics</h2>
        <div className="grid md:grid-cols-3 gap-3 mt-4">
          {data.topics.length === 0 && <EmptyState />}
          {data.topics.map((t) => (
            <div key={`${t.subject}-${t.topic}`} className="p-4 border-2 border-black rounded-xl bg-white">
              <div className="text-xs uppercase text-neutral-500 font-bold">{t.subject}</div>
              <div className="font-bold text-lg">{t.topic}</div>
              <div className="h-2 mt-2 bg-neutral-200 rounded-full overflow-hidden border-2 border-black">
                <div className="h-full bg-[var(--mint)]" style={{ width: `${t.mastery}%` }} />
              </div>
              <div className="text-xs mt-1 text-neutral-600">{t.mastery}% mastery · {t.attempts} tries</div>
            </div>
          ))}
        </div>
      </div>

      <BadgesPanel />
    </div>
  );
}

function StatCard({ icon, label, value, bg, testid }) {
  return (
    <div data-testid={testid} className={`nb-card p-5 ${bg}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase font-bold text-neutral-600">{label}</div>
        <div className="w-8 h-8 rounded-lg border-2 border-black flex items-center justify-center bg-white">{icon}</div>
      </div>
      <div className="text-4xl font-extrabold mt-2">{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-8 text-neutral-500">
      <div className="text-lg font-bold">No data yet</div>
      <div className="text-sm">Answer a few questions in Practice to see your map light up.</div>
    </div>
  );
}
